// Topic-specific calculator landing pages that funnel into /claim-calculator.
// Each targets a high-intent "<damage> claim calculator" search and routes
// the visitor into the main multi-step calculator.

import { images } from "../mock";

export const calcFunnelPages = {
  "roof-claim-calculator": {
    slug: "roof-claim-calculator",
    damageKey: "roof",
    eyebrow: "Florida Roof Claim Calculator",
    h1: "Roof Claim Calculator — What Is Your Florida Roof Claim Worth?",
    h1Highlight: "What Is Your Florida Roof Claim Worth?",
    intro:
      "Use our free Florida roof claim calculator to estimate what your roof damage claim should actually pay — before you accept the carrier's number. It walks through roof material, square footage, gutters, and hidden damage line-by-line and returns a low–high recovery range built from current Florida adjuster pricing. No cost, and no contact info until you want the breakdown.",
    image: images.roof,
    metaTitle:
      "Roof Claim Calculator Florida | Estimate Your Roof Damage Payout | Claim Beast",
    metaDescription:
      "Free Florida roof claim calculator. Estimate your hail, wind, or storm roof damage claim value in 60 seconds using real Florida adjuster pricing, then get a free review from a licensed Tampa Bay public adjuster.",
    keywords:
      "roof claim calculator, roof damage claim calculator florida, roof insurance claim estimate florida, roof replacement claim value florida, hail roof claim calculator, wind roof claim estimate tampa",
    ctaLabel: "Estimate My Roof Claim",
    calloutHeadline: "Ready to see your roof claim number?",
    calloutBody:
      "Our calculator walks through your roof material, size, gutters, and hidden decking damage line-by-line, then returns a low–high recovery range built from current Florida roofing costs — so you can sanity-check whatever the carrier puts on paper.",
    whatTitle: "What The Roof Claim Calculator Factors In",
    whatItems: [
      "Roof material (asphalt shingle, tile, metal, or flat)",
      "Total roof size, calculated from your home's square footage",
      "Full tear-off and replacement — not a partial-slope patch",
      "Damaged gutters, downspouts, and flashing",
      "Hidden underlayment and decking damage carriers routinely miss",
      "Florida code-upgrade requirements (secondary water barrier, hurricane straps)",
    ],
    stepsTitle: "How The Roof Claim Calculator Works",
    steps: [
      { n: "01", title: "Select roof as your damage type", text: "Tell us your roof material and the square footage of your home — we auto-calculate roof size." },
      { n: "02", title: "Add the details", text: "Note damaged gutters, whether you're unsure on any number (we substitute the FL average), and any interior water intrusion." },
      { n: "03", title: "Get your low–high range", text: "We calculate a defensible roof claim value from current Florida replacement-cost pricing — with a 'likely recovery' figure." },
      { n: "04", title: "We review it free", text: "A licensed Florida public adjuster reviews your estimate and tells you whether the carrier is underpaying — no upfront cost." },
    ],
    faqs: [
      {
        question: "How accurate is a roof claim calculator?",
        answer:
          "A roof claim calculator gives you a credible low–high replacement-cost range based on current Florida roofing labor and material prices. It's designed to sanity-check the carrier's offer — not to replace a licensed adjuster physically inspecting your roof. If your calculated range is well above the carrier's number, that's a strong signal you're being underpaid.",
      },
      {
        question: "What is the average roof insurance claim payout in Florida?",
        answer:
          "It varies widely by roof size, material, and the extent of damage — a partial repair might be a few thousand dollars, while a full replacement with code upgrades on a large tile roof can exceed $40,000. The calculator estimates your specific range rather than a generic average, then a public adjuster verifies it.",
      },
      {
        question: "Does the roof calculator work for denied or underpaid roof claims?",
        answer:
          "Yes — that's when it's most useful. If your carrier denied the claim or paid less than the calculator's low estimate, you likely have grounds to dispute. Florida allows reopening claims, supplemental claims, and appraisal on most policies, and we handle all three.",
      },
    ],
    related: [
      { text: "Roof claim denied in Florida?", href: "/roof-claim-denial-florida" },
      { text: "Hail damage claim help", href: "/hail-damage" },
      { text: "Wind / hurricane damage claim help", href: "/wind-damage" },
      { text: "Denied insurance claim in Florida?", href: "/denied-insurance-claim-florida" },
      { text: "Underpaid insurance claim in Florida?", href: "/underpaid-insurance-claim-florida" },
      { text: "Water Damage Claim Calculator", href: "/water-damage-claim-calculator" },
    ],
  },
  "water-damage-claim-calculator": {
    slug: "water-damage-claim-calculator",
    damageKey: "interior",
    eyebrow: "Florida Water Damage Claim Calculator",
    h1: "Water Damage Claim Calculator — Estimate Your Florida Payout",
    h1Highlight: "Estimate Your Florida Payout",
    intro:
      "Use our free Florida water damage claim calculator to estimate what a pipe burst, appliance leak, roof leak, or storm water intrusion claim should actually pay. It accounts for drywall, flooring, cabinets, mold remediation, and contents line-by-line using current Florida pricing — and returns a low–high recovery range with no contact info required until you want the breakdown.",
    image: images.flood,
    metaTitle:
      "Water Damage Claim Calculator Florida | Estimate Your Payout | Claim Beast",
    metaDescription:
      "Free Florida water damage claim calculator. Estimate pipe burst, leak, and storm water damage claim value in 60 seconds using real Florida pricing, then get a free review from a licensed Tampa Bay public adjuster.",
    keywords:
      "water damage claim calculator, water damage claim estimate florida, pipe burst claim calculator, water damage insurance payout florida, water leak claim value florida, flood water damage claim calculator tampa",
    ctaLabel: "Estimate My Water Damage Claim",
    calloutHeadline: "See what your water damage claim should pay.",
    calloutBody:
      "Water claims get underpaid constantly — carriers miss drywall, subfloor, cabinetry, and mold. Our calculator adds it all up using current Florida restoration pricing and returns a low–high range you can hold the carrier's estimate against.",
    whatTitle: "What The Water Damage Calculator Factors In",
    whatItems: [
      "Number of rooms affected and severity per room",
      "Drywall replacement, repainting, and trim",
      "Flooring type and square footage damaged (carpet, LVP, tile, hardwood)",
      "Cabinets, countertops, and built-ins",
      "Mold remediation scope (contained room vs. whole-home / HVAC)",
      "Damaged contents and personal property",
    ],
    stepsTitle: "How The Water Damage Claim Calculator Works",
    steps: [
      { n: "01", title: "Select your damage types", text: "Interior water damage, cabinets, contents — pick everything the leak or flooding affected." },
      { n: "02", title: "Answer a few quick questions", text: "Rooms affected, severity, flooring, and mold. Unsure on a number? We use the Florida average." },
      { n: "03", title: "Get your low–high range", text: "We calculate a defensible water damage claim value from current Florida restoration pricing." },
      { n: "04", title: "We review it free", text: "A licensed public adjuster checks whether the carrier is calling your loss 'wear and tear' or underpaying it." },
    ],
    faqs: [
      {
        question: "How is a water damage insurance claim calculated in Florida?",
        answer:
          "A water damage claim is calculated from the replacement cost of everything the water touched — drywall, insulation, flooring, cabinetry, and contents — plus drying, demolition, and any mold remediation. Carriers frequently miss hidden damage to subfloors and cabinet interiors, which is where a public adjuster recovers the most.",
      },
      {
        question: "Will insurance cover my water damage claim in Florida?",
        answer:
          "Sudden and accidental water damage (a burst pipe, a failed water heater, an overflowing appliance) is covered under most Florida homeowners policies. Carriers often try to deny these as 'constant or repeated seepage' or 'wear and tear' — a plumber report and proper documentation defeat that. Flood (rising water) requires separate NFIP or private flood coverage.",
      },
      {
        question: "What if my water damage claim was already underpaid?",
        answer:
          "Run your loss through the calculator — if the result is above the carrier's payment, you likely have a supplemental claim. Florida allows supplemental and reopened claims within statutory windows, and we routinely recover the missed drywall, flooring, cabinetry, and mold scope.",
      },
    ],
    related: [
      { text: "Water damage claim help", href: "/water-damage" },
      { text: "Mold damage claim help", href: "/mold-damage" },
      { text: "Flood damage claim help", href: "/flood-damage" },
      { text: "Underpaid insurance claim in Florida?", href: "/underpaid-insurance-claim-florida" },
      { text: "Denied insurance claim in Florida?", href: "/denied-insurance-claim-florida" },
      { text: "Roof Claim Calculator", href: "/roof-claim-calculator" },
    ],
  },
};
